import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TestRunResultView from '../components/TestRunResultView';
import { API_BASE_URL } from '../api';
import { canManageTestRuns } from '../permissions';
import './TestExecutionPage.css';

/**
 * Standalone page for a single test run, reachable via a shareable link
 * (/test-runs/:runId). Any authenticated user can open the route; the backend
 * decides whether they may see the run (testruns:read).
 */
function TestRunResultsPage({ user }) {
  const { runId } = useParams();
  const navigate = useNavigate();
  const canManage = canManageTestRuns(user);
  const [runResults, setRunResults] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | forbidden | notfound | error

  const fetchRun = () => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE_URL}/testruns/${runId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  const handleEditLinks = async ({ ticketUrl, prUrl }) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/testruns/${runId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ticketUrl, prUrl }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to update the links.');
    }
    const refreshed = await fetchRun();
    if (refreshed.ok) {
      setRunResults(await refreshed.json());
    }
  };

  const handleEditResult = async (resultId, { status, comment }) => {
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${API_BASE_URL}/testruns/${runId}/results/${resultId}`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, comment }),
      }
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.error || 'Failed to update the result.');
    }
    const refreshed = await fetchRun();
    if (refreshed.ok) {
      setRunResults(await refreshed.json());
    }
  };

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');

    fetch(`${API_BASE_URL}/testruns/${runId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (cancelled) return null;
        if (response.status === 401) {
          try {
            sessionStorage.setItem('postLoginRedirect', `/test-runs/${runId}`);
          } catch (_) { /* ignore */ }
          navigate('/login');
          return null;
        }
        if (response.status === 403) {
          setStatus('forbidden');
          return null;
        }
        if (response.status === 404) {
          setStatus('notfound');
          return null;
        }
        if (!response.ok) {
          setStatus('error');
          return null;
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled || !data) return;
        setRunResults(data);
        setStatus('ok');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [runId, navigate]);

  return (
    <div className="test-execution-page">
      <div className="container">
        <h1>Test Run Results</h1>

        {status === 'loading' && <p className="help-text">Loading test run…</p>}

        {status === 'forbidden' && (
          <div className="empty-state">
            <p>You don’t have permission to view this test run.</p>
            <p className="empty-state-action-subtext">
              Ask an admin to grant you access, then open the link again.
            </p>
          </div>
        )}

        {status === 'notfound' && (
          <div className="empty-state">
            <p>This test run could not be found.</p>
          </div>
        )}

        {status === 'error' && (
          <div className="empty-state">
            <p>Something went wrong loading this test run. Try again.</p>
          </div>
        )}

        {status === 'ok' && (
          <TestRunResultView
            runResults={runResults}
            onBack={() => navigate('/test-execution')}
            shareUrl={`${window.location.origin}/test-runs/${runId}`}
            onEditResult={canManage ? handleEditResult : undefined}
            onEditLinks={canManage ? handleEditLinks : undefined}
          />
        )}
      </div>
    </div>
  );
}

export default TestRunResultsPage;
